/**
 * requestTracker — the page's count of requests a person is waiting on (WEB-APP-VERSION-REFRESH-001).
 *
 * A reload is safe only when nothing a person started is still on the wire: reloading mid-save drops a change the
 * person believes was made. The facade (`createApiFacade`) is the ONE seam every web request crosses, so it begins an
 * entry for each request and ends it when the request settles; the page's reload-safety gate reads the count.
 *
 * A `{ background: true }` request (a poll) is never counted: a poll in flight must not hold a reload back, or a page
 * that polls every few seconds could never update.
 *
 * @module @puffin-core/api-client/requestTracker
 */

/**
 * @returns {Readonly<{
 *   begin: () => () => void,
 *   getInFlight: () => number,
 *   subscribe: (listener: (inFlight: number) => void) => () => void,
 * }>}
 */
export function createRequestTracker() {
  let inFlight = 0;
  const listeners = new Set();

  const publish = () => {
    for (const listener of listeners) listener(inFlight);
  };

  return Object.freeze({
    /**
     * Count one request as started.
     *
     * @returns {() => void} its ending — idempotent, so a request ends exactly once however often it is called
     */
    begin() {
      inFlight += 1;
      publish();
      let ended = false;
      return () => {
        if (ended) return;
        ended = true;
        inFlight -= 1;
        publish();
      };
    },

    /** @returns {number} the requests in flight now */
    getInFlight() {
      return inFlight;
    },

    /**
     * @param {(inFlight: number) => void} listener - told the count after every change
     * @returns {() => void} unsubscribe
     * @throws {TypeError} when `listener` is not a function
     */
    subscribe(listener) {
      if (typeof listener !== 'function') {
        throw new TypeError('requestTracker.subscribe: `listener` must be a function.');
      }
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  });
}

let pageTracker = null;

/**
 * The page's tracker — one per document, shared by every facade on the page.
 *
 * @returns {ReturnType<typeof createRequestTracker>}
 */
export function getPageRequestTracker() {
  if (!pageTracker) pageTracker = createRequestTracker();
  return pageTracker;
}
